import { useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Key } from 'ts-key-enum';
import { useSnapshot } from 'valtio';

import FrontDoor from '@cartwhl/ui/feature/FrontDoor';
import useClickOutside from '@cartwhl/ui/hooks/useClickOutside';
import CameraIconAlternative from '@cartwhl/ui/icons/CameraIconAlternative';
import KeyboardIcon from '@cartwhl/ui/icons/KeyboardIcon';
import RedoIcon from '@cartwhl/ui/icons/RedoIcon';
import UndoIcon from '@cartwhl/ui/icons/UndoIcon';

import { posingStore, uiStore } from 'stores';

import ImageToPose from './ImageToPose';
import TextToPose from './TextToPose';

export default function PoseFrontDoor() {
  const { poseFrontDoorMode } = useSnapshot(uiStore.state);
  const { posingSystem } = useSnapshot(posingStore.state);
  const { historyStack, historyCursor } = useSnapshot(posingStore.state);

  const containerRef = useRef<HTMLDivElement>(null);

  const hasUndo = historyCursor > 0;
  const hasRedo = historyCursor < historyStack.length - 1;

  const handleUndo = () => {
    posingSystem.current?.poseUndoRedo(true);
  };

  const handleRedo = () => {
    posingSystem.current?.poseUndoRedo(false);
  };

  useHotkeys(
    Key.Escape,
    () => {
      if (poseFrontDoorMode === 'project') return;
      uiStore.setPoseFrontDoorMode('project');
    },
    {
      preventDefault: true,
      enableOnFormTags: true,
    },
  );
  useHotkeys(['Meta+z', 'ctrl+z'], handleUndo, {
    preventDefault: true,
  });
  useHotkeys(['Meta+shift+z', 'ctrl+shift+z'], handleRedo, {
    preventDefault: true,
  });

  // On first run, set the mode to prompt to auto open the text-to-pose prompt bar
  useEffect(() => {
    uiStore.setPoseFrontDoorMode('text');
  }, []);

  useClickOutside(containerRef, () => {
    uiStore.setPoseFrontDoorMode('project');
  });

  return (
    <FrontDoor.Container
      ref={containerRef}
      showX={poseFrontDoorMode !== 'project'}
      onXClick={() => uiStore.setPoseFrontDoorMode('project')}
      className="absolute bottom-0 lg:relative"
    >
      <FrontDoor.FadeContent
        show={poseFrontDoorMode === 'image'}
        className="w-[600px] max-w-full"
      >
        <ImageToPose />
      </FrontDoor.FadeContent>

      <FrontDoor.FadeContent
        show={poseFrontDoorMode === 'text'}
        className="w-[600px] max-w-full"
      >
        <TextToPose />
      </FrontDoor.FadeContent>

      <FrontDoor.FadeContent
        show={poseFrontDoorMode === 'project'}
        className="flex flex-wrap gap-2"
      >
        {/* <FrontDoor.Button
          variant="studio/body/sm"
          icon={<Heart value={false} />}
          onClick={() => {}}
          rounded="first"
          isActive={false}
        /> */}
        <div className="flex gap-px">
          <FrontDoor.Button
            variant="secondary"
            text="Undo"
            icon={<UndoIcon className="relative bottom-px h-4 w-4" />}
            onClick={handleUndo}
            isActive={false}
            disabled={!hasUndo}
            rounded="first"
            className="flex-none rounded-r-none"
          />
          <FrontDoor.Button
            variant="secondary"
            text="Redo"
            iconEnd={<RedoIcon className="relative bottom-px h-4 w-4" />}
            onClick={handleRedo}
            isActive={false}
            disabled={!hasRedo}
            className="flex-none rounded-l-none"
          />
        </div>

        <FrontDoor.Divider className="hidden lg:block" />

        <FrontDoor.Button
          variant="primary"
          text="Image to pose"
          icon={<CameraIconAlternative className="relative bottom-[1px]" />}
          onClick={() => {
            uiStore.setPoseFrontDoorMode('image');
          }}
          isActive={false}
          isWide
          className="w-full flex-none md:w-auto md:flex-auto"
        />

        <FrontDoor.Button
          variant="primary"
          text="Text to pose"
          icon={<KeyboardIcon className="relative bottom-px" />}
          onClick={() => {
            uiStore.setPoseFrontDoorMode('text');
          }}
          isActive={false}
          rounded="last"
          isWide
          className="w-full flex-none md:w-auto md:flex-auto"
        />
      </FrontDoor.FadeContent>
    </FrontDoor.Container>
  );
}
